// Las alertas del expediente como UNA lista de cosas por hacer, en orden de urgencia: el cliente que
// ya pagó y va a quedar bloqueado va primero. El texto sale de aquí; la ficha solo lo pinta.
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { CUTOFF_REFUTED_NOTE, LOCK_REFUTED_NOTE } from './logic';
import type { MdmDeviceAlert, MdmDeviceAlertCode, MdmFleetDevice } from './types';

export type MdmAlertTone = 'danger' | 'warn';

export interface MdmAlertLine {
    code: string;
    tone: MdmAlertTone;
    text: string;
    /** El porqué, para el `?` de la línea. */
    help?: string;
}

/** Orden de urgencia; un código desconocido va al final. */
const ALERT_ORDER: readonly MdmDeviceAlertCode[] = [
    'PAID_OFF_WITH_LIVE_CUT',
    'LOCK_REFUTED',
    'LOCK_RETRY_EXHAUSTED',
    'LOCK_CONFIRMATION_OVERDUE',
    'UNLOCK_CONFIRMATION_OVERDUE',
];

function paidOffText(alert: MdmDeviceAlert, now: Date | string): string {
    const cutAt = alert.lockScheduledFor;
    if (!cutAt) return 'Ya terminó de pagar y su equipo conserva un corte programado.';
    const when = formatDateTimeMx(cutAt);
    return new Date(cutAt).getTime() > new Date(now).getTime()
        ? `Ya terminó de pagar y su equipo se va a bloquear el ${when}.`
        : `Ya terminó de pagar y su equipo tenía un corte programado para el ${when}.`;
}

/** En un ACTIVE lo desmentido es el corte: el expediente nunca dijo bloqueado. */
function refutedLine(alert: MdmDeviceAlert, device: MdmFleetDevice, since: string): MdmAlertLine {
    return device.mdmStatus === LoanDeviceMdmStatusEnum.ACTIVE
        ? {
              code: alert.code,
              tone: 'danger',
              text: `El proveedor desmintió el corte el ${since}: el equipo sigue sin candado.`,
              help: CUTOFF_REFUTED_NOTE,
          }
        : {
              code: alert.code,
              tone: 'danger',
              text: `El proveedor desmintió el bloqueo el ${since}: el equipo quedó sin candado.`,
              help: LOCK_REFUTED_NOTE,
          };
}

function toLine(alert: MdmDeviceAlert, device: MdmFleetDevice, now: Date | string): MdmAlertLine {
    const since = formatDateTimeMx(alert.since);
    switch (alert.code) {
        case 'PAID_OFF_WITH_LIVE_CUT':
            return { code: alert.code, tone: 'danger', text: paidOffText(alert, now) };
        case 'LOCK_REFUTED':
            return refutedLine(alert, device, since);
        case 'LOCK_RETRY_EXHAUSTED':
            return {
                code: alert.code,
                tone: 'danger',
                text: `El proveedor rechazó el bloqueo y se agotaron los reintentos el ${since}.`,
            };
        case 'LOCK_CONFIRMATION_OVERDUE':
            return {
                code: alert.code,
                tone: 'warn',
                text: `El proveedor no ha confirmado el bloqueo; su plazo venció el ${since}.`,
            };
        case 'UNLOCK_CONFIRMATION_OVERDUE':
            return {
                code: alert.code,
                tone: 'warn',
                text: `El proveedor no ha confirmado el desbloqueo; su plazo venció el ${since}.`,
            };
        default:
            return {
                code: String(alert.code),
                tone: 'warn',
                text: `Hay una alerta que esta pantalla todavía no sabe leer (${String(alert.code)}) desde el ${since}.`,
            };
    }
}

function rank(code: string): number {
    const index = ALERT_ORDER.indexOf(code as MdmDeviceAlertCode);
    return index === -1 ? ALERT_ORDER.length : index;
}

/** Las cosas por hacer de este equipo, la más urgente primero. Vacío si no hay ninguna. */
export function readDeviceAlerts(device: MdmFleetDevice, now: Date | string): MdmAlertLine[] {
    return (device.mdmAlerts ?? [])
        .map((alert) => toLine(alert, device, now))
        .sort((a, b) => rank(a.code) - rank(b.code));
}

/** `true` cuando el desmentido ya viaja como alerta y la lista lo cuenta. */
export function hasLockRefutedAlert(device: MdmFleetDevice): boolean {
    return (device.mdmAlerts ?? []).some((alert) => alert.code === 'LOCK_REFUTED');
}
